import '../../styles/restScreens.css';
import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';

// ============================================================
// SubtaskPanel — checklist items for a task (public.task_checklist)
// Checking/unchecking recalculates the parent task's progress_pct
// automatically via the Postgres trigger (see migration 002, Gate 3) —
// this component just refetches after a change so the panel reflects it.
// ============================================================

export default function SubtaskPanel({ taskId, editable = true, theme = 'light', onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    const { data, error } = await supabaseClient
      .from('task_checklist')
      .select('*')
      .eq('parent_task_id', taskId)
      .order('created_at', { ascending: true });
    if (!error) setItems(data || []);
    setLoading(false);
  }, [taskId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  async function toggleItem(item) {
    const { error } = await supabaseClient
      .from('task_checklist')
      .update({ completed: !item.completed, completed_date: !item.completed ? new Date().toISOString() : null })
      .eq('id', item.id);
    if (!error) {
      await loadItems();
      onChanged && onChanged();
    }
  }

  async function addItem() {
    const title = newTitle.trim();
    if (!title) return;
    setSaving(true);
    const { error } = await supabaseClient
      .from('task_checklist')
      .insert([{ parent_task_id: taskId, title }]);
    setSaving(false);
    if (!error) {
      setNewTitle('');
      await loadItems();
      onChanged && onChanged();
    }
  }

  async function deleteItem(item) {
    const { error } = await supabaseClient.from('task_checklist').delete().eq('id', item.id);
    if (!error) {
      await loadItems();
      onChanged && onChanged();
    }
  }

  const completedCount = items.filter(i => i.completed).length;

  return (
    <div data-m6-layout="tasks" data-m6-component="SubtaskPanel" className={`rounded-lg border p-4 ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
          Subtasks {items.length > 0 && `(${completedCount}/${items.length})`}
        </h4>
      </div>

      {loading ? (
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
      ) : items.length === 0 ? (
        <div className={`text-sm italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No subtasks yet.</div>
      ) : (
        <ul className="space-y-2 mb-3">
          {items.map(item => (
            <li key={item.id} className="flex items-center gap-2 group">
              {editable
                ? <label className="ia-subtask-check"><input
                  aria-label={`Complete ${item.title}`}
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleItem(item)}
                  className="w-4 h-4 accent-[var(--accent-ink)] cursor-pointer"
                /></label>
                : <span aria-label={item.completed ? 'Completed' : 'Not completed'}>{item.completed ? '✓' : '○'}</span>}
              <span
                className={`flex-1 text-sm ${item.completed ? 'line-through text-gray-400' : theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
              >
                {item.title}
              </span>
              {editable && (
                <button
                  onClick={() => deleteItem(item)}
                  className="opacity-0 group-hover:opacity-100 text-xs text-red-500 hover:text-red-600 transition-opacity"
                  title="Remove subtask"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
            placeholder="Add a subtask..."
            className={`flex-1 text-sm rounded-md border px-3 py-1.5 ${
              theme === 'dark' ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800'
            }`}
          />
          <button
            onClick={addItem}
            disabled={saving || !newTitle.trim()}
            className="text-sm px-3 py-1.5 rounded-md bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
